"use client";

import React, { useEffect, useRef, useState } from "react";

interface AnimatedBackgroundProps {
  width?: string | number;
  height?: string | number;
  eyesLeftMargin?: number; // percentage of component width
  eyesTopMargin?: number; // percentage of component height
  eyeSpacingPercent?: number; // spacing between eyes as percentage of container width
  eyeSizePercent?: number; // eye size as percentage of container width
  pupilSizeRatio?: number; // pupil size as ratio of eye size (0.45 = 45% of eye size)
  pupilXOffset?: number; // horizontal offset for pupil default position (0.5 = centered, lower = more left)
  pupilYOffset?: number; // vertical offset for pupil default position (0.5 = centered, lower = higher up)
  backgroundImagePath?: string;
  eyeImagePath?: string;
  pupilImagePath?: string;
}

const AnimatedBackground: React.FC<AnimatedBackgroundProps> = ({
  width = "100%",
  height = "100vh",
  eyesLeftMargin = 70, // default to 70% from left
  eyesTopMargin = 15, // default to 15% from top
  eyeSpacingPercent = 1.5, // default spacing between eyes (1.5% of container width)
  eyeSizePercent = 3, // default eye size (3% of container width)
  pupilSizeRatio = 0.45, // pupil is 45% of eye size
  pupilXOffset = 0.45, // slightly left of center
  pupilYOffset = 0.5, // centered vertically
  backgroundImagePath = "/assets/eyeroll-images/landing-content.png",
  eyeImagePath = "/assets/eyeroll-images/eye.png",
  pupilImagePath = "/assets/eyeroll-images/pupil.png",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const pupil1Ref = useRef<HTMLSpanElement>(null);
  const pupil2Ref = useRef<HTMLSpanElement>(null);
  const eye1Ref = useRef<HTMLDivElement>(null);
  const eye2Ref = useRef<HTMLDivElement>(null);

  // State to track container dimensions and pupil parameters
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    eyeSize: 0,
    eyeSpacing: 0,
    pupilSize: 0,
    pupilXOffset,
    pupilYOffset,
  });

  // Update dimensions on mount and resize
  useEffect(() => {
    const updateDimensions = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      // Use the props parameters for sizing
      const eyeSize = Math.max((containerWidth * eyeSizePercent) / 100, 20); // min 20px
      const eyeSpacing = (containerWidth * eyeSpacingPercent) / 100;
      const pupilSize = eyeSize * pupilSizeRatio;

      setDimensions({
        width: containerWidth,
        height: containerHeight,
        eyeSize,
        eyeSpacing,
        pupilSize,
        pupilXOffset,
        pupilYOffset,
      });
    };

    // Initial update
    updateDimensions();

    // Handle resize
    window.addEventListener("resize", updateDimensions);

    // Setup ResizeObserver for more specific container resizing
    if (typeof ResizeObserver !== "undefined" && containerRef.current) {
      const observer = new ResizeObserver(updateDimensions);
      observer.observe(containerRef.current);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", updateDimensions);
      };
    }

    return () => window.removeEventListener("resize", updateDimensions);
  }, [
    eyeSizePercent,
    eyeSpacingPercent,
    pupilSizeRatio,
    pupilXOffset,
    pupilYOffset,
  ]);

  // Handle mouse movement for eye tracking
  useEffect(() => {
    if (!dimensions.width) return; // Skip if dimensions not initialized yet

    const pupil1 = pupil1Ref.current;
    const pupil2 = pupil2Ref.current;
    const eye1 = eye1Ref.current;
    const eye2 = eye2Ref.current;

    if (!pupil1 || !pupil2 || !eye1 || !eye2) return;

    // Function to get absolute position of an element
    const getPosition = (element: HTMLElement) => {
      const rect = element.getBoundingClientRect();
      return {
        x: rect.left + window.scrollX + element.offsetWidth / 2,
        y: rect.top + window.scrollY + element.offsetHeight / 2,
      };
    };

    const moveEyes = (e: MouseEvent) => {
      // Get eye centers
      const eye1Center = getPosition(eye1);
      const eye2Center = getPosition(eye2);

      // Calculate max movement radius (30% of eye size)
      const maxRadius = dimensions.eyeSize * 0.3;

      // Calculate direction vectors from eye centers to cursor
      const dx1 = e.pageX - eye1Center.x;
      const dy1 = e.pageY - eye1Center.y;
      const dx2 = e.pageX - eye2Center.x;
      const dy2 = e.pageY - eye2Center.y;

      // Calculate distances
      const distance1 = Math.sqrt(dx1 * dx1 + dy1 * dy1);
      const distance2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);

      // Calculate pupil offsets with max radius limit
      const x1 = distance1 <= maxRadius ? dx1 : (dx1 / distance1) * maxRadius;
      const y1 = distance1 <= maxRadius ? dy1 : (dy1 / distance1) * maxRadius;
      const x2 = distance2 <= maxRadius ? dx2 : (dx2 / distance2) * maxRadius;
      const y2 = distance2 <= maxRadius ? dy2 : (dy2 / distance2) * maxRadius;

      // Apply transforms - add the offsets from base position
      pupil1.style.transform = `translate(calc(-50% + ${x1}px), calc(-50% + ${y1}px))`;
      pupil2.style.transform = `translate(calc(-50% + ${x2}px), calc(-50% + ${y2}px))`;
    };

    window.addEventListener("mousemove", moveEyes);

    return () => {
      window.removeEventListener("mousemove", moveEyes);
    };
  }, [dimensions]);

  // Convert width and height props to CSS values
  const widthValue = typeof width === "number" ? `${width}px` : width;
  const heightValue = typeof height === "number" ? `${height}px` : height;

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: widthValue,
        height: heightValue,
        overflow: "hidden",
      }}
    >
      {/* Background Image */}
      <div
        style={{
          width: "100%",
          height: "100%",
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 10,
        }}
      >
        <img
          src={backgroundImagePath}
          alt="Background"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>

      {/* Eyes - position and size are based on container dimensions */}
      {dimensions.width > 0 && (
        <div
          style={{
            position: "absolute",
            left: `${eyesLeftMargin}%`,
            top: `${eyesTopMargin}%`,
            zIndex: 20,
            pointerEvents: "none",
            width: `${dimensions.eyeSize * 2 + dimensions.eyeSpacing}px`,
            height: `${dimensions.eyeSize}px`,
            transform: "rotate(-30deg)",
          }}
        >
          {/* First eye */}
          <div
            ref={eye1Ref}
            style={{
              background: `url(${eyeImagePath}) no-repeat`,
              backgroundSize: "contain",
              width: `${dimensions.eyeSize}px`,
              height: `${dimensions.eyeSize}px`,
              position: "absolute",
              left: 0,
              top: 0,
              zIndex: 21,
            }}
          >
            <span
              ref={pupil1Ref}
              style={{
                background: `url(${pupilImagePath}) no-repeat`,
                backgroundSize: "contain",
                display: "block",
                width: `${dimensions.pupilSize}px`,
                height: `${dimensions.pupilSize}px`,
                position: "absolute",
                top: "50%",
                left: "45%", // Move slightly to the left (was 50%)
                transform: "translate(-50%, -50%)",
                zIndex: 22,
                transition: "transform 0.05s ease-out",
              }}
            />
          </div>

          {/* Second eye */}
          <div
            ref={eye2Ref}
            style={{
              background: `url(${eyeImagePath}) no-repeat`,
              backgroundSize: "contain",
              width: `${dimensions.eyeSize}px`,
              height: `${dimensions.eyeSize}px`,
              position: "absolute",
              left: `${dimensions.eyeSize + dimensions.eyeSpacing}px`,
              top: 0,
              zIndex: 21,
            }}
          >
            <span
              ref={pupil2Ref}
              style={{
                background: `url(${pupilImagePath}) no-repeat`,
                backgroundSize: "contain",
                display: "block",
                width: `${dimensions.pupilSize}px`,
                height: `${dimensions.pupilSize}px`,
                position: "absolute",
                top: "50%",
                left: "45%", // Move slightly to the left (was 50%)
                transform: "translate(-50%, -50%)",
                zIndex: 22,
                transition: "transform 0.05s ease-out",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AnimatedBackground;
